import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Card,
  Chip,
  CircularProgress,
  IconButton,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import RefreshIcon from '@mui/icons-material/Refresh';
import PointOfSaleIcon from '@mui/icons-material/PointOfSale';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import { pavoApiService, type PavoDevice } from '../../../../services/pavoApiService';
import { useTranslation } from 'react-i18next';

interface PavoP2PDevicesModalProps {
  open: boolean;
  onClose: () => void;
  companyId?: string;
  branchId?: string;
}

const deviceModeLabel = (mode: string | undefined, t: (key: string) => string): string => {
  switch (mode) {
    case 'pavo_order': return t('pavoP2PDevicesModal.deviceModes.orderTerminal');
    case 'pavo_quicksale': return t('pavoP2PDevicesModal.deviceModes.quickSale');
    case 'pavo_restaurant': return t('pavoP2PDevicesModal.deviceModes.restaurant');
    default: return mode || '-';
  }
};

const PavoP2PDevicesModal: React.FC<PavoP2PDevicesModalProps> = ({
  open,
  onClose,
  companyId,
  branchId,
}) => {
  const { t } = useTranslation('salesSystem');
  const [devices, setDevices] = useState<PavoDevice[]>([]);
  const [loading, setLoading] = useState(false);

  const loadDevices = useCallback(async () => {
    if (!companyId) return;
    setLoading(true);
    try {
      const devices = await pavoApiService.getPavoDevices(companyId, branchId);
      devices.sort((a, b) => (a.pavoNumber ?? 999) - (b.pavoNumber ?? 999));
      setDevices(devices);
    } catch (e) {
      console.error('[PavoP2PDevicesModal] Cihazlar yüklenemedi:', e);
      setDevices([]);
    } finally {
      setLoading(false);
    }
  }, [companyId, branchId]);

  useEffect(() => {
    if (open) loadDevices();
  }, [open, loadDevices]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <PointOfSaleIcon sx={{ color: '#7c4dff' }} />
          <Typography variant="h6" component="span">
            {t('pavoP2PDevicesModal.title')}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <IconButton size="small" onClick={loadDevices} disabled={loading}>
            <RefreshIcon fontSize="small" />
          </IconButton>
          <IconButton size="small" onClick={onClose}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {t('pavoP2PDevicesModal.description')}
        </Typography>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={32} />
          </Box>
        ) : devices.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography variant="body2" color="text.secondary">
              {companyId
                ? t('pavoP2PDevicesModal.emptyBranch')
                : t('pavoP2PDevicesModal.companyMissing')}
            </Typography>
          </Box>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            {devices.map((device) => (
              <Card key={device.deviceCode} variant="outlined" sx={{ p: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Box
                      sx={{
                        width: 36,
                        height: 36,
                        borderRadius: '50%',
                        bgcolor: '#7c4dff',
                        color: '#fff',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontWeight: 700,
                        fontSize: 14,
                        flexShrink: 0,
                      }}
                    >
                      #{device.pavoNumber ?? '?'}
                    </Box>
                    <Box>
                      <Typography variant="body2" fontWeight={600}>
                        {device.deviceName}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {deviceModeLabel(device.deviceMode, t)} • {device.deviceCode}
                      </Typography>
                    </Box>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Chip
                      icon={
                        <FiberManualRecordIcon
                          sx={{ fontSize: 10, color: device.isOnline ? 'success.main' : 'text.disabled' }}
                        />
                      }
                      label={device.isOnline ? 'Online' : 'Offline'}
                      size="small"
                      variant="outlined"
                      color={device.isOnline ? 'success' : 'default'}
                    />
                  </Box>
                </Box>
              </Card>
            ))}
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>{t('pavoP2PDevicesModal.close')}</Button>
      </DialogActions>
    </Dialog>
  );
};

export default PavoP2PDevicesModal;
